import { useEffect, useRef } from 'react';

import { useEventCallback } from '@gitbook/util-react-hooks';

const SPINE_OVERLAY_SELECTOR = '[data-spine-overlay]';
const UIKIT_POPOVER_SELECTOR = '[data-popover-overlay]';
const UIKIT_PALETTE_SELECTOR = '[data-palette-overlay]';
const TOAST_SELECTOR = '[data-sonner-toast]';
// Opt-in marker on any element that should NOT count as "outside" an open
// overlay (modal/popover/palette). Use this for floating UI that lives in a
// portal but logically belongs to the overlay below it (e.g. a format
// toolbar over selected text inside a Dialog).
const IGNORE_OVERLAY_CLOSE_SELECTOR = '[data-ignore-overlay-close]';
const ANY_POPOVER_SELECTOR = [
    SPINE_OVERLAY_SELECTOR,
    UIKIT_POPOVER_SELECTOR,
    UIKIT_PALETTE_SELECTOR,
].join(',');
const ANY_EXCEPT_SPINE_OVERLAY_SELECTOR = [
    UIKIT_POPOVER_SELECTOR,
    UIKIT_PALETTE_SELECTOR,
    TOAST_SELECTOR,
    IGNORE_OVERLAY_CLOSE_SELECTOR,
].join(',');

/**
 * Get the Reflag feedback dialog: a `<dialog>` when modal, a `[popover]` when anchored.
 */
function getReflagDialogElement(): Element | null {
    return (
        document
            .getElementById('reflag-feedback-dialog-container')
            ?.shadowRoot?.querySelector('dialog,[popover]') ?? null
    );
}

/**
 * Check if the Reflag feedback dialog is open, whichever element the SDK rendered.
 */
function checkIsReflagDialogOpen(element: Element): boolean {
    if (element instanceof HTMLDialogElement) {
        return element.open;
    }
    // `:popover-open` throws on browsers without the Popover API.
    return 'showPopover' in element && element.matches(':popover-open');
}

/**
 * Check if an element is part of an overlay.
 */
export function checkIsElementContainsInOverlay(element: Element) {
    if (element.closest(ANY_EXCEPT_SPINE_OVERLAY_SELECTOR)) {
        return true;
    }

    if (getReflagDialogElement()?.contains(element)) {
        return true;
    }

    return false;
}

/**
 * Get all the open popovers, including the Reflag feedback dialog.
 */
function getOpenPopovers(): Element[] {
    const popovers = Array.from(document.querySelectorAll(ANY_POPOVER_SELECTOR));
    const reflagDialog = getReflagDialogElement();
    if (reflagDialog && checkIsReflagDialogOpen(reflagDialog)) {
        popovers.push(reflagDialog);
    }
    return popovers;
}

/**
 * Check if a popover is open.
 */
function checkIsPopoverOpen(args: { ignored: Element[] }) {
    return getOpenPopovers().some((element) => !args.ignored.includes(element));
}

/**
 * Check if a popover is open and allows to pass a `onClose`
 * callback to be notified when it's closed.
 */
export function useCheckIsPopoverOpen(props: { isDisabled: boolean }) {
    const { isDisabled } = props;
    const mouseUpCleanupRef = useRef<(() => void) | null>(null);
    const ignoredRef = useRef<Element[]>([]);
    useEffect(() => {
        if (!isDisabled) {
            ignoredRef.current = getOpenPopovers();
        }
    }, [isDisabled]);
    useEffect(() => {
        if (isDisabled) {
            mouseUpCleanupRef.current?.();
        }
    }, [isDisabled]);
    useEffect(() => () => mouseUpCleanupRef.current?.(), []);
    return useEventCallback((options?: { onClose?: () => void }) => {
        const onClose = options?.onClose;
        if (checkIsPopoverOpen({ ignored: ignoredRef.current })) {
            mouseUpCleanupRef.current?.();

            // When a mouse up event is triggered, we reevaluate if the popover is open.
            const handleMouseUp = () => {
                // Popover is closed, we cleanup the event listener.
                if (!checkIsPopoverOpen({ ignored: ignoredRef.current })) {
                    mouseUpCleanupRef.current?.();
                    onClose?.();
                }
            };
            document.body.addEventListener('mouseup', handleMouseUp);
            mouseUpCleanupRef.current = () => {
                document.body.removeEventListener('mouseup', handleMouseUp);
                mouseUpCleanupRef.current = null;
            };
            return true;
        }
        return false;
    });
}
